import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { calculateScores, getScoreLabel, getScoreInsight, getLevelFromPoints, getTimeMultiplier } from '../lib/scores'
import { useAuth } from '../hooks/useAuth'
import { updateStreak } from '../lib/streak'
import GoalsTab from '../components/GoalsTab'
import HabitsTab from '../components/HabitsTab'
import OnboardingTutorial from '../components/OnboardingTutorial'

const AREA_DISPLAY = {
  health: 'Health', mind: 'Mind', career: 'Career',
  relationships: 'Relationships', faith: 'Faith', purpose: 'Goals',
}

const AREA_COLORS = {
  health: '#4caf7d', mind: '#9b5ce0', career: '#c9a84c',
  relationships: '#e05c5c', faith: '#5c8fe0', purpose: '#e07d3c',
}

const AREA_META = {
  health:        { icon: '💪', color: '#4caf7d' },
  mind:          { icon: '🧠', color: '#9b5ce0' },
  career:        { icon: '📈', color: '#c9a84c' },
  relationships: { icon: '❤️', color: '#e05c5c' },
  faith:         { icon: '✝️', color: '#5c8fe0' },
  purpose:       { icon: '🎯', color: '#e07d3c' },
}

function getLocalDate() {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default function Dashboard({ onNavigate }) {
  const { user, profile, signOut } = useAuth()
  const [scores, setScores] = useState(null)
  const [history, setHistory] = useState([])
  const [saving, setSaving] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [quickAction, setQuickAction] = useState(null) // 'task' | 'todo'
  // Quick task state
  const [quickDesc, setQuickDesc] = useState('')
  const [quickTime, setQuickTime] = useState('')
  const [quickEvaluating, setQuickEvaluating] = useState(false)
  const [quickPreview, setQuickPreview] = useState(null)
  const [quickSaving, setQuickSaving] = useState(false)
  const [quickError, setQuickError] = useState('')
  // Quick todo state
  const [quickTodo, setQuickTodo] = useState('')
  const [quickTodoAdding, setQuickTodoAdding] = useState(false)
  const [currentStreak, setCurrentStreak] = useState(0)
  const [currentTab, setCurrentTab] = useState('tasks')
  const [manualPoints, setManualPoints] = useState({})
  const [displayScore, setDisplayScore] = useState(0)
  const [userLevel, setUserLevel] = useState(1)
  const [showAddGoal, setShowAddGoal] = useState(false)
  const [showAddHabit, setShowAddHabit] = useState(false)
  const [manualRefresh, setManualRefresh] = useState(0)
  const [todos, setTodos] = useState([])
  const [tasks, setTasks] = useState([])

  useEffect(() => {
    if (user) { loadHistory(); loadStreak(); loadManualPoints(); loadUserLevel(); loadTodos(); loadDayTasks() }
  }, [user])

  useEffect(() => {
    setScores(calculateScores({}, {}, manualPoints, currentStreak || profile?.streak || 0))
  }, [manualPoints, currentStreak])


  // Animate score - instant drop, smooth increase
  const prevScore = React.useRef(0)
  useEffect(() => {
    const target = scores?.overall_score ?? 0
    const prev = prevScore.current
    prevScore.current = target

    // If score dropped or is zero, set immediately — no animation
    if (target <= prev || target === 0) {
      setDisplayScore(target)
      return
    }

    // Only animate upward
    const duration = 800
    const startTime = performance.now()
    let animFrame

    function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3) }

    function animate(currentTime) {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      setDisplayScore(Math.round(prev + easeOutCubic(progress) * (target - prev)))
      if (progress < 1) {
        animFrame = requestAnimationFrame(animate)
      } else {
        setDisplayScore(target)
      }
    }

    animFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animFrame)
  }, [scores?.overall_score])

  async function loadHistory() {
    const { data } = await supabase
      .from('checkins')
      .select('date, overall_score')
      .eq('user_id', user.id)
      .order('date', { ascending: false })
      .limit(7)
    if (data) setHistory(data.reverse())
  }

  async function loadStreak() {
    const streak = await updateStreak(user.id)
    setCurrentStreak(streak)
  }

  async function loadManualPoints() {
    const { data } = await supabase.from('manual_tasks')
      .select('area, final_points').eq('user_id', user.id).eq('date', getLocalDate()).eq('flagged', false)
    if (data) {
      const pts = {}
      data.forEach(t => { pts[t.area] = (pts[t.area] || 0) + t.final_points })
      setManualPoints(pts)
    }
  }

  async function loadUserLevel() {
    const { data } = await supabase.from('manual_tasks')
      .select('area, final_points, date').eq('user_id', user.id).eq('flagged', false)
    if (data && data.length > 0) {
      const byDate = {}
      data.forEach(t => {
        if (!byDate[t.date]) byDate[t.date] = { health: 0, mind: 0, career: 0, relationships: 0, faith: 0, purpose: 0 }
        if (byDate[t.date][t.area] !== undefined) byDate[t.date][t.area] += t.final_points
      })
      const totalPoints = Object.values(byDate).reduce((sum, d) => {
        const areas = [d.health, d.mind, d.career, d.relationships, d.faith, d.purpose]
        return sum + Math.min(100, Math.round(areas.reduce((a, b) => a + b, 0) / 6))
      }, 0)
      setUserLevel(getLevelFromPoints(totalPoints).level)
    }
  }

  async function loadTodos() {
    const { data } = await supabase.from('todos')
      .select('*').eq('user_id', user.id).eq('date', getLocalDate())
      .order('completed', { ascending: true }).order('created_at', { ascending: true })
    setTodos(data || [])
  }

  async function loadDayTasks() {
    const { data } = await supabase.from('manual_tasks')
      .select('*').eq('user_id', user.id).eq('date', getLocalDate())
      .eq('flagged', false).order('created_at', { ascending: false })
    setTasks(data || [])
  }

  async function deleteTodoItem(todo) {
    await supabase.from('todos').delete().eq('id', todo.id)
    if (todo.completed) {
      await supabase.from('manual_tasks').delete()
        .eq('user_id', user.id).eq('date', getLocalDate())
        .eq('description', todo.text).eq('category', 'To-Do')
    }
    setTodos(prev => prev.filter(t => t.id !== todo.id))
    loadManualPoints()
  }

  const [confirmDelete, setConfirmDelete] = useState(null)

  async function deleteTaskItem(task) {
    setConfirmDelete(task)
  }

  async function confirmDeleteTask() {
    const task = confirmDelete
    setConfirmDelete(null)
    await supabase.from('manual_tasks').delete().eq('id', task.id)
    const updated = tasks.filter(t => t.id !== task.id)
    setTasks(updated)
    // Recalculate score instantly from remaining tasks
    const pts = {}
    updated.filter(t => !t.flagged).forEach(t => {
      pts[t.area] = (pts[t.area] || 0) + t.final_points
    })
    setManualPoints(pts)
  }

  function applyCurve(raw) {
    if (raw <= 0) return 0
    if (raw >= 100) return 100
    return Math.min(100, Math.round(Math.pow(raw / 100, 0.75) * 100))
  }

  function openQuickAction(action) {
    setMenuOpen(false)
    setQuickAction(action)
    setQuickDesc('')
    setQuickTime('')
    setQuickPreview(null)
    setQuickError('')
    setQuickTodo('')
  }

  async function quickEvaluate() {
    if (!quickDesc.trim()) return
    setQuickEvaluating(true)
    setQuickError('')
    try {
      const res = await fetch('/api/evaluate-task', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: quickDesc.trim(), timeSpent: quickTime, userId: user?.id })
      })
      const data = await res.json()
      if (data.error) { setQuickError(data.error); setQuickEvaluating(false); return }
      const finalPoints = data.flagged ? 0 : data.base_points
      setQuickPreview({ ...data, finalPoints })
    } catch (e) { setQuickError('Something went wrong.') }
    setQuickEvaluating(false)
  }

  async function quickSaveTask() {
    if (!quickPreview) return
    setQuickSaving(true)
    const { data: newTask } = await supabase.from('manual_tasks').insert({
      user_id: user.id, date: getLocalDate(),
      description: quickDesc.trim(),
      category: quickPreview.category, area: quickPreview.area,
      base_points: quickPreview.base_points, time_minutes: null,
      final_points: quickPreview.finalPoints, flagged: quickPreview.flagged,
      flag_reason: quickPreview.flag_reason || '',
    }).select().single()
    if (newTask) {
      setTasks(prev => [newTask, ...prev])
      // Update score instantly
      setManualPoints(prev => ({
        ...prev,
        [quickPreview.area]: (prev[quickPreview.area] || 0) + quickPreview.finalPoints
      }))
    }
    setManualRefresh(r => r + 1)
    setQuickAction(null)
    setQuickSaving(false)
  }

  async function quickAddTodo() {
    if (!quickTodo.trim()) return
    setQuickTodoAdding(true)
    await supabase.from('todos').insert({
      user_id: user.id, date: getLocalDate(),
      text: quickTodo.trim(), completed: false, points: 10, area: 'purpose'
    })
    setManualRefresh(r => r + 1)
    loadTodos()
    setQuickAction(null)
    setQuickTodoAdding(false)
  }

  const firstName = profile?.full_name?.split(' ')[0] || 'there'
  const scoreLabel = scores ? getScoreLabel(scores.overall_score) : null
  const userGoals = profile?.goals || []

  return (
    <div style={{ ...s.page, background: '#111118', color: '#e8e6df' }}>
      <OnboardingTutorial userId={user?.id} onComplete={() => { loadManualPoints(); setManualRefresh(r => r + 1) }} />
      <div style={s.ambient} />
      <div style={s.ambientBottom} />

      {/* NAV */}
      <nav style={{ ...s.nav, background: 'rgba(6,6,8,0.85)' }}>
        <div style={s.logo}>LIFESCORE</div>
        <div style={s.navRight}>
          <div style={s.streakPill}>🔥 <span style={s.streakNum}>{currentStreak || profile?.streak || 0}</span> day streak</div>
          <div style={s.levelPill}>LVL <span style={s.streakNum}>{userLevel}</span></div>
          <div style={s.avatar} onClick={() => onNavigate('profile')} title="Profile">
            {profile?.avatar_url
              ? <img src={profile.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              : firstName[0].toUpperCase()
            }
          </div>
        </div>
      </nav>

      <main style={s.main}>

        {/* HERO SCORE */}
        <div style={s.hero}>
          <div style={s.heroLabel}>Your score today</div>
          <div style={s.ringWrap}>
            {(() => {
              const score = displayScore
              const scoreColor = scores?.overall_score === 0 ? '#6b6860' : (scores?.overall_score || 0) >= 75 ? '#4caf7d' : (scores?.overall_score || 0) >= 50 ? '#c9a84c' : '#e05c5c'
              const glowColor = scores?.overall_score === 0 ? 'transparent' : (scores?.overall_score || 0) >= 75 ? 'rgba(76,175,125,0.4)' : (scores?.overall_score || 0) >= 50 ? 'rgba(201,168,76,0.4)' : 'rgba(224,92,92,0.4)'
              return (
                <>
                  <svg width="190" height="190" viewBox="0 0 190 190" style={{ transform: 'rotate(-90deg)', position: 'absolute' }}>
                    <defs>
                      <linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#8b6a1a" />
                        <stop offset="100%" stopColor="#f0cc7a" />
                      </linearGradient>
                    </defs>
                    <circle cx="95" cy="95" r="85" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
                    <circle
                      cx="95" cy="95" r="85" fill="none"
                      stroke="url(#g)" strokeWidth="6" strokeLinecap="round"
                      strokeDasharray="534"
                      strokeDashoffset={534 - (534 * score / 100)}
                      style={{ filter: 'drop-shadow(0 0 8px rgba(201,168,76,0.5))' }}
                    />
                  </svg>
                  <div style={s.ringCenter}>
                    <div style={{
                      ...s.scoreNum,
                      color: scoreColor,
                      textShadow: score > 0 ? `0 0 20px ${glowColor}, 0 0 40px ${glowColor}` : 'none',
                      
                      transition: 'color 0.5s, text-shadow 0.5s',
                    }}>{score}</div>
                  </div>
                </>
              )
            })()}
          </div>
          {scoreLabel && (
            <div style={{ ...s.scoreLabel, color: scoreLabel.color }}>{scoreLabel.label}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
            {scores?.perfect_day && <div style={s.bonusBadge}>✦ Perfect Day +10%</div>}
            {(currentStreak || 0) >= 3 && <div style={s.bonusBadge}>🔥 Streak {Math.round(((scores?.streak_multiplier || 1) - 1) * 100)}% boost</div>}
          </div>
        </div>

        {/* TASKS / GOALS / HABITS TAB SWITCHER */}
        <div style={s.tabRow}>
          <div style={{ ...s.tab, background: currentTab === 'habits' ? '#c9a84c' : 'rgba(255,255,255,0.06)', color: currentTab === 'habits' ? '#000' : '#6b6860', fontWeight: currentTab === 'habits' ? '700' : '400' }}
            onClick={() => setCurrentTab('habits')}>
            Habits
          </div>
          <div style={{ ...s.tab, background: currentTab === 'tasks' ? '#c9a84c' : 'rgba(255,255,255,0.06)', color: currentTab === 'tasks' ? '#000' : '#6b6860', fontWeight: currentTab === 'tasks' ? '700' : '400' }}
            onClick={() => setCurrentTab('tasks')}>
            Tasks
          </div>
          <div style={{ ...s.tab, background: currentTab === 'goals' ? '#c9a84c' : 'rgba(255,255,255,0.06)', color: currentTab === 'goals' ? '#000' : '#6b6860', fontWeight: currentTab === 'goals' ? '700' : '400' }}
            onClick={() => setCurrentTab('goals')}>
            Goals
          </div>
        </div>

        {/* HABITS TAB */}
        {currentTab === 'habits' && (
          <HabitsTab
            userId={user?.id}
            onPointsUpdated={() => { loadManualPoints(); loadDayTasks() }}
            showAdd={showAddHabit}
            onAddClose={() => setShowAddHabit(false)}
          />
        )}

        {/* TASKS TAB */}
        {currentTab === 'tasks' && (
          <div style={s.floatSection}>
            <div style={s.floatTitle}>Tasks</div>
            {tasks.length === 0
              ? <div style={s.floatEmpty}>Log a task to earn points</div>
              : tasks.map((task, i) => (
                <div key={task.id} style={{ ...s.floatRow, borderBottom: i < tasks.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ ...s.listAreaBadge, background: `${AREA_COLORS[task.area]}18`, color: AREA_COLORS[task.area] }}>{task.category}</div>
                    <div style={s.listText}>{task.description}</div>
                    {task.time_minutes ? <div style={s.listMeta}>{task.time_minutes}</div> : null}
                  </div>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', color: '#4caf7d', marginRight: '8px' }}>+{applyCurve(task.final_points)}</div>
                  <div style={s.listX} onClick={() => deleteTaskItem(task)}>✕</div>
                </div>
              ))
            }
          </div>
        )}

        {/* GOALS TAB */}
        {currentTab === 'goals' && (
          <GoalsTab userId={user?.id} showAdd={showAddGoal} onAddClose={() => setShowAddGoal(false)} />
        )}

      </main>
      {/* INVISIBLE OVERLAY — closes menu on outside tap */}
      {quickAction && (
        <div onClick={() => setQuickAction(null)} style={{ position: 'fixed', inset: 0, zIndex: 150 }} />
      )}

      {/* QUICK ACTION FULL SCREEN OVERLAYS */}
      {quickAction && (
        <div style={s.qaOverlay}>
          <div style={s.qaScreen}>

            {/* LOG TASK */}
            {quickAction === 'task' && (
              <>
                <div style={s.qaHeader}>
                  <div style={s.qaTitle}>Log a Task</div>
                  <div style={s.qaClose} onClick={() => setQuickAction(null)}>✕</div>
                </div>
                <div style={s.qaHint}>Be specific — more detail = more points</div>
                <textarea style={s.qaTextarea}
                  placeholder="e.g. Read 20 pages of Atomic Habits, called my mom for 30 mins..."
                  value={quickDesc}
                  onChange={e => { setQuickDesc(e.target.value); setQuickPreview(null) }}
                  rows={3} autoFocus />
                <div style={s.qaTimeLabel}>How long did this take?</div>
                <input
                  style={s.qaInput}
                  placeholder="e.g. 45 minutes, 2 hours, all day..."
                  value={quickTime}
                  onChange={e => { setQuickTime(e.target.value); setQuickPreview(null) }}
                />
                {quickError && <div style={s.qaError}>{quickError}</div>}
                {!quickPreview ? (
                  <div style={{ ...s.qaBtn, opacity: quickEvaluating || !quickDesc.trim() ? 0.5 : 1 }} onClick={quickEvaluate}>
                    {quickEvaluating ? 'Analyzing...' : 'Analyze Task →'}
                  </div>
                ) : (
                  <div style={s.qaPreview}>
                    <div style={s.qaPreviewTop}>
                      <div style={{ ...s.qaPreviewArea, color: AREA_COLORS[quickPreview.area] }}>{quickPreview.category} · {quickPreview.area}</div>
                      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', color: '#4caf7d' }}>+{quickPreview.finalPoints} pts</div>
                    </div>
                    <div style={s.qaPreviewReason}>{quickPreview.reasoning}</div>
                    <div style={s.qaPreviewActions}>
                      <div style={s.qaSecondary} onClick={() => setQuickPreview(null)}>Re-analyze</div>
                      <div style={{ ...s.qaBtn, flex: 2, opacity: quickSaving ? 0.6 : 1 }} onClick={quickSaveTask}>
                        {quickSaving ? 'Saving...' : 'Save Task →'}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

          </div>
        </div>
      )}

      {/* CONFIRM DELETE BUBBLE */}
      {confirmDelete && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', zIndex: 400 }} onClick={() => setConfirmDelete(null)} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', padding: '28px 24px', zIndex: 401, width: '320px', textAlign: 'center' }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '2px', color: '#e8e6df', marginBottom: '8px' }}>Remove Task?</div>
            <div style={{ fontSize: '13px', color: '#6b6860', marginBottom: '24px', lineHeight: '1.5' }}>"{confirmDelete.description}"</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1, padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', color: '#6b6860', fontSize: '14px', cursor: 'pointer', textAlign: 'center' }}
                onClick={() => setConfirmDelete(null)}>Cancel</div>
              <div style={{ flex: 1, padding: '12px', background: 'rgba(224,92,92,0.15)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '12px', color: '#e05c5c', fontSize: '14px', fontWeight: '600', cursor: 'pointer', textAlign: 'center' }}
                onClick={confirmDeleteTask}>Remove</div>
            </div>
          </div>
        </>
      )}

      {/* DARK OVERLAY when menu open */}
      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 198, backdropFilter: 'blur(4px)' }} />
      )}

      {/* DROP-UP MENU — floating text, no box */}
      {menuOpen && (
        <div style={{ position: 'fixed', bottom: '100px', left: 0, right: 0, padding: '0 32px', zIndex: 200, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
          {[
            { label: 'Log Task',      sub: 'Describe & score a task', action: () => openQuickAction('task') },
            { label: 'Add Habit',     sub: 'Build a daily habit',     action: () => { setMenuOpen(false); setCurrentTab('habits'); setShowAddHabit(true) } },
            { label: 'Start Goal',    sub: 'Set a new goal',           action: () => { setMenuOpen(false); onNavigate('addgoal') } },
            { label: 'Log Workout',   sub: 'Track your exercise',     action: () => { setMenuOpen(false); onNavigate('fitness') } },
            { label: 'Daily Journal', sub: 'Reflect on your day',     action: () => { setMenuOpen(false); onNavigate('journal') } },
          ].map((item, i) => (
            <div key={i}
              style={{ opacity: item.action ? 1 : 0.3, cursor: item.action ? 'pointer' : 'default', padding: '6px 0' }}
              onClick={() => item.action?.()}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#ffffff', letterSpacing: '-0.5px', lineHeight: 1.1 }}>{item.label}</div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>{item.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* BOTTOM NAV */}
      <div style={s.bottomNav}>
        <div style={{ ...s.navItem, opacity: 1 }} onClick={() => { setMenuOpen(false); setQuickAction(null) }}>
          <span style={{ ...s.navLabel, color: '#c9a84c', fontWeight: '600' }}>Score</span>
          <div style={s.navDot} />
        </div>
        <div style={{ ...s.navItem, opacity: 0.45 }} onClick={() => { setMenuOpen(false); setQuickAction(null); onNavigate('friends') }}>
          <span style={s.navLabel}>Friends</span>
        </div>
        <div style={{ ...s.navItem, opacity: 0.45 }} onClick={() => { setMenuOpen(false); setQuickAction(null); onNavigate('insights') }}>
          <span style={s.navLabel}>Insights</span>
        </div>
        <div style={{ ...s.navItem, opacity: 0.45 }} onClick={() => { setMenuOpen(false); setQuickAction(null); onNavigate('coach') }}>
          <span style={s.navLabel}>Coach</span>
        </div>
        <div style={{ ...s.navItem, opacity: 0.45 }} onClick={() => { setMenuOpen(false); setQuickAction(null); onNavigate('profile') }}>
          <span style={s.navLabel}>Settings</span>
        </div>
      </div>

      {/* FIXED + BUTTON */}
      <div style={s.fixedPlusBtn} onClick={() => { setQuickAction(null); setMenuOpen(prev => !prev) }}>
        <span style={{ fontSize: '28px', color: '#000', lineHeight: 1, fontWeight: '300', display: 'block', transition: 'transform 0.3s ease', transform: menuOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}>+</span>
      </div>
    </div>
  )
}
const s = {
  page: { minHeight: '100vh', background: '#111118', color: '#e8e6df', fontFamily: "'DM Sans', sans-serif", position: 'relative', overflowX: 'hidden' },
  ambient: { position: 'fixed', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'none', pointerEvents: 'none', zIndex: 0 },
  ambientBottom: { position: 'fixed', bottom: '-20%', left: '50%', transform: 'translateX(-50%)', width: '600px', height: '600px', background: 'none', pointerEvents: 'none', zIndex: 0 },
  nav: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', background: 'rgba(6,6,8,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.12)' },
  logo: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '3px', color: '#c9a84c' },
  navRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  streakPill: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6b6860', background: 'rgba(255,255,255,0.07)', padding: '6px 12px', borderRadius: '20px' },
  streakNum: { color: '#c9a84c', fontWeight: '500' },
  levelPill: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6b6860', background: 'rgba(255,255,255,0.07)', padding: '6px 10px', borderRadius: '20px' },
  avatar: { width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', color: '#000', cursor: 'pointer' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '28px 20px 100px', position: 'relative', zIndex: 1 },
  hero: { textAlign: 'center', padding: '20px 0 28px' },
  heroLabel: { fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '20px' },
  ringWrap: { position: 'relative', width: '190px', height: '190px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  ringCenter: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' },
  scoreNum: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '72px', lineHeight: 1, letterSpacing: '2px', transition: 'color 0.5s, text-shadow 0.5s' },
  scoreLabel: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', letterSpacing: '3px', marginBottom: '8px' },
  bonusBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#c9a84c', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)', padding: '4px 10px', borderRadius: '20px' },
  gettingStarted: { background: 'linear-gradient(135deg, rgba(201,168,76,0.08), rgba(201,168,76,0.03))', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '16px', padding: '20px', marginBottom: '20px' },
  gsTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '2px', color: '#f0cc7a', marginBottom: '6px' },
  gsBody: { fontSize: '13px', color: '#6b6860', marginBottom: '14px' },
  gsList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  gsItem: { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: '#e8e6df', lineHeight: '1.4' },
  gsNum: { background: 'rgba(201,168,76,0.2)', color: '#c9a84c', fontWeight: '700', fontSize: '11px', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
  floatSection: { marginBottom: '32px' },
  floatTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '2px', color: '#e8e6df', marginBottom: '12px', paddingLeft: '4px' },
  floatRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 4px' },
  floatEmpty: { fontSize: '13px', color: '#6b6860', fontStyle: 'italic', paddingLeft: '4px', paddingBottom: '8px' },
  listSection: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', overflow: 'hidden', marginBottom: '16px' },
  listTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', letterSpacing: '2px', color: '#e8e6df', padding: '14px 16px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  listEmpty: { padding: '16px', fontSize: '13px', color: '#6b6860', fontStyle: 'italic' },
  listRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px' },
  listX: { fontSize: '11px', color: '#6b6860', cursor: 'pointer', padding: '4px', flexShrink: 0 },
  listText: { fontSize: '14px', color: '#e8e6df', lineHeight: '1.4' },
  listMeta: { fontSize: '11px', color: '#6b6860', marginTop: '2px' },
  listCheck: { width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' },
  listAreaBadge: { display: 'inline-block', fontSize: '10px', fontWeight: '600', letterSpacing: '0.5px', padding: '2px 7px', borderRadius: '5px', marginBottom: '3px', textTransform: 'uppercase' },
  tabRow: { display: 'flex', flexDirection: 'row', gap: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '14px', padding: '4px', marginBottom: '16px' },
  tab: { flex: 1, padding: '11px', borderRadius: '10px', textAlign: 'center', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' },
  goalsEmpty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', textAlign: 'center', gap: '12px' },
  goalsEmptyIcon: { fontSize: '48px' },
  goalsEmptyTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: '#e8e6df' },
  goalsEmptyText: { fontSize: '14px', color: '#6b6860', lineHeight: '1.6', maxWidth: '260px' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '14px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#6b6860' },
  sectionAction: { fontSize: '12px', color: '#c9a84c' },
  card: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '20px' },
  chartBars: { display: 'flex', alignItems: 'flex-end', gap: '6px', height: '70px', marginBottom: '8px' },
  barWrap: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end' },
  bar: { width: '100%', borderRadius: '4px 4px 2px 2px', transition: 'height 0.8s ease' },
  barDay: { fontSize: '10px' },
  areasGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  areaCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '16px 14px', position: 'relative', overflow: 'hidden' },
  areaTop: { position: 'absolute', top: 0, left: 0, right: 0, height: '2px' },
  areaIcon: { fontSize: '20px', marginBottom: '8px' },
  areaName: { fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '6px' },
  areaScoreRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  areaScore: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '26px', letterSpacing: '1px', lineHeight: 1 },
  areaBarBg: { height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', marginTop: '10px', overflow: 'hidden' },
  areaBarFill: { height: '100%', borderRadius: '2px', transition: 'width 0.8s ease' },
  insightCard: { background: '#1a1a26', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '16px', padding: '20px' },
  insightEyebrow: { fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#c9a84c', marginBottom: '10px' },
  insightBubble: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', background: 'linear-gradient(135deg, rgba(201,168,76,0.08), rgba(201,168,76,0.03))', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '14px', padding: '14px 16px', marginBottom: '20px' },
  insightLeft: { display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1 },
  insightDot: { width: '7px', height: '7px', borderRadius: '50%', background: '#c9a84c', boxShadow: '0 0 6px rgba(201,168,76,0.6)', flexShrink: 0, marginTop: '5px' },
  insightText: { fontSize: '13px', color: '#e8e6df', lineHeight: '1.5' },
  insightCoachBtn: { fontSize: '12px', fontWeight: '700', color: '#c9a84c', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '8px', padding: '7px 12px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  bottomNav: { position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(6,6,8,0.95)', backdropFilter: 'blur(20px)', borderTop: '1px solid rgba(255,255,255,0.12)', display: 'flex', justifyContent: 'space-around', alignItems: 'center', paddingTop: '12px', paddingBottom: 'max(22px, env(safe-area-inset-bottom))', zIndex: 100 },
  navItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' },
  navDot: { width: '4px', height: '4px', borderRadius: '50%', background: '#c9a84c' },
  navLabel: { fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860' },
  fixedPlusBtn: { position: 'fixed', bottom: '90px', right: '20px', width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #c9a84c, #a07828)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(201,168,76,0.45)', cursor: 'pointer', zIndex: 201 },
  qaOverlay: { position: 'fixed', inset: 0, zIndex: 600, backdropFilter: 'blur(8px)', background: 'rgba(13,13,20,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  qaScreen: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', padding: '24px', width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', gap: '14px' },
  qaHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  qaTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '2px', color: '#e8e6df' },
  qaClose: { fontSize: '18px', color: '#6b6860', cursor: 'pointer', padding: '4px' },
  qaHint: { fontSize: '13px', color: '#6b6860', lineHeight: '1.5', marginTop: '-6px' },
  qaTextarea: { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', padding: '14px', color: '#e8e6df', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", outline: 'none', resize: 'none', lineHeight: '1.5', boxSizing: 'border-box' },
  qaTimeLabel: { fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860' },
  qaTimeRow: { display: 'flex', gap: '6px' },
  qaTimeChip: { flex: 1, padding: '8px 4px', borderRadius: '8px', fontSize: '12px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s' },
  qaError: { fontSize: '12px', color: '#e05c5c', background: 'rgba(224,92,92,0.08)', borderRadius: '8px', padding: '10px 12px' },
  qaInput: { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', padding: '14px', color: '#e8e6df', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box' },
  qaBtn: { padding: '14px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', fontSize: '15px', fontWeight: '700' },
  qaPreview: { background: 'rgba(76,175,125,0.06)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '6px' },
  qaPreviewTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  qaPreviewArea: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  qaPreviewReason: { fontSize: '13px', color: '#6b6860', lineHeight: '1.5' },
  qaPreviewActions: { display: 'flex', gap: '8px', marginTop: '4px' },
  qaSecondary: { flex: 1, padding: '11px', background: 'rgba(255,255,255,0.05)', color: '#6b6860', borderRadius: '10px', textAlign: 'center', cursor: 'pointer', fontSize: '13px' },
  menuRow: { display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', borderRadius: '10px', background: 'rgba(255,255,255,0.03)', cursor: 'pointer' },
  menuIcon: { width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  menuTitle: { fontSize: '14px', fontWeight: '600', color: '#e8e6df' },
  menuSub: { fontSize: '11px', color: '#6b6860', marginTop: '1px' },
  menuArrow: { marginLeft: 'auto', color: '#6b6860', fontSize: '14px' },
}
